// Drill-down panel: one user's spend, opened from a row in the per-user cost
// table. Fetches GET /cost/users/{user_id} for the same time window the table
// is showing, then lets the admin view it three ways:
//   • Sessions — every conversation, session by session (the raw payload).
//   • By day / By month — client-side rollups bucketed by each session's
//     last_activity. (The backend has no time-bucketed cost endpoint, so this
//     is derived here. A session's whole cost lands in its last_activity
//     bucket — exact for same-day sessions, a close approximation otherwise.)

import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { getUserCostDetail } from "../../../Services/adminReporting";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../../ui/sheet";
import { Skeleton } from "../../../ui/skeleton";
import { cn } from "../../../lib/utils";
import {
  ChannelBadge,
  TokenBreakdown,
  fmtDateTime,
  fmtDuration,
  fmtNum,
  fmtUsd,
} from "./costFormat";

const VIEWS = [
  ["sessions", "Sessions"],
  ["day", "By day"],
  ["month", "By month"],
];

// Bucket sessions by their last_activity into day (YYYY-MM-DD) or month
// (YYYY-MM) groups, summing cost and splitting voice vs chat. Returns rows
// sorted most-recent-first to match the sessions view.
const bucketSessions = (sessions, mode) => {
  const buckets = new Map();
  sessions.forEach((s) => {
    const d = new Date(s.last_activity);
    if (Number.isNaN(d.getTime())) return;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const key = mode === "month" ? `${y}-${m}` : `${y}-${m}-${day}`;
    const label =
      mode === "month"
        ? d.toLocaleString(undefined, { month: "short", year: "numeric" })
        : d.toLocaleString(undefined, {
            month: "short",
            day: "numeric",
            year: "numeric",
          });
    const cur =
      buckets.get(key) ||
      { key, label, total: 0, voice: 0, chat: 0, sessions: 0 };
    const cost = Number(s.cost_usd) || 0;
    cur.total += cost;
    cur.sessions += 1;
    if (s.channel === "voice") cur.voice += cost;
    else cur.chat += cost;
    buckets.set(key, cur);
  });
  return Array.from(buckets.values()).sort((a, b) =>
    b.key.localeCompare(a.key)
  );
};

const UserCostDrawer = ({ userId, windowDays, range, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState("sessions");
  const [openSessions, setOpenSessions] = useState(() => new Set());

  const toggleSession = (id) =>
    setOpenSessions((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  useEffect(() => {
    if (!userId) return undefined;
    let active = true;
    setLoading(true);
    setError(null);
    setData(null);
    setView("sessions");
    setOpenSessions(new Set());
    getUserCostDetail(userId, windowDays, range)
      .then((payload) => {
        if (active) setData(payload);
      })
      .catch((err) => {
        if (active)
          setError(err?.response?.data?.message || "Failed to load user cost.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [userId, windowDays, range]);

  const sessions = useMemo(() => data?.sessions || [], [data]);
  const grouped = useMemo(
    () => (view === "sessions" ? [] : bucketSessions(sessions, view)),
    [sessions, view]
  );

  return (
    <Sheet open={!!userId} onOpenChange={(open) => (!open ? onClose() : null)}>
      <SheetContent className="flex flex-col gap-0 p-0">
        <SheetHeader className="border-b px-6 py-4">
          <SheetTitle className="text-base">User spend</SheetTitle>
          <p className="font-mono text-xs text-muted-foreground">{userId}</p>
          {data?.window ? (
            <p className="text-xs text-muted-foreground">
              {data.window.start} → {data.window.end}
            </p>
          ) : null}
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : error ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
              {error}
            </div>
          ) : (
            <>
              <div className="mb-5 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Total spend · window
                </div>
                <div className="mt-1 text-2xl font-semibold tabular-nums">
                  {fmtUsd(data?.total_cost_usd)}
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {sessions.length} session{sessions.length === 1 ? "" : "s"}
                </div>
              </div>

              {/* View toggle: raw conversations vs day/month rollups. */}
              <div className="mb-4 inline-flex rounded-md border bg-muted/40 p-0.5">
                {VIEWS.map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setView(key)}
                    className={cn(
                      "rounded-[5px] px-3 py-1 text-xs font-medium transition-colors",
                      view === key
                        ? "bg-background shadow-sm text-foreground"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {sessions.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  No sessions in this window.
                </p>
              ) : view === "sessions" ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-xs text-muted-foreground">
                        <th className="w-6 py-2 pr-1" />
                        <th className="py-2 pr-3 font-medium">Channel</th>
                        <th className="py-2 px-3 font-medium">Module</th>
                        <th className="py-2 px-3 font-medium">Model</th>
                        <th className="py-2 px-3 text-right font-medium">Turns</th>
                        <th className="py-2 px-3 text-right font-medium">Length</th>
                        <th className="py-2 px-3 text-right font-medium">Cost</th>
                        <th className="py-2 pl-3 text-right font-medium">Last activity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sessions.map((s) => {
                        const isOpen = openSessions.has(s.session_id);
                        return (
                          <React.Fragment key={s.session_id}>
                            <tr
                              onClick={() => toggleSession(s.session_id)}
                              className="cursor-pointer border-b transition-colors hover:bg-slate-50"
                            >
                              <td className="py-2 pr-1 align-top">
                                <button
                                  type="button"
                                  aria-label={isOpen ? "Hide tokens" : "Show tokens"}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleSession(s.session_id);
                                  }}
                                  className="rounded p-0.5 text-muted-foreground hover:bg-slate-200 hover:text-foreground"
                                >
                                  {isOpen ? (
                                    <ChevronDown className="h-4 w-4" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4" />
                                  )}
                                </button>
                              </td>
                              <td className="py-2 pr-3 align-top">
                                <ChannelBadge channel={s.channel} />
                              </td>
                              <td className="py-2 px-3 align-top text-xs uppercase tracking-wide text-muted-foreground">
                                {s.module || "—"}
                              </td>
                              <td className="py-2 px-3 align-top font-mono text-xs">
                                {s.model || "—"}
                              </td>
                              <td className="py-2 px-3 text-right align-top tabular-nums">
                                {fmtNum(s.turns)}
                              </td>
                              <td className="py-2 px-3 text-right align-top tabular-nums">
                                {s.channel === "voice" ? fmtDuration(s.duration_seconds) : "—"}
                              </td>
                              <td className="py-2 px-3 text-right align-top font-medium tabular-nums">
                                {fmtUsd(s.cost_usd)}
                              </td>
                              <td className="py-2 pl-3 text-right align-top text-xs text-muted-foreground">
                                {fmtDateTime(s.last_activity)}
                              </td>
                            </tr>
                            {isOpen ? (
                              <tr className="border-b bg-slate-50/60">
                                <td />
                                <td colSpan={7} className="py-3 pr-3">
                                  <TokenBreakdown tokens={s.tokens} label="Tokens" />
                                </td>
                              </tr>
                            ) : null}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-xs text-muted-foreground">
                          <th className="py-2 pr-3 font-medium">
                            {view === "month" ? "Month" : "Day"}
                          </th>
                          <th className="py-2 px-3 text-right font-medium">Sessions</th>
                          <th className="py-2 px-3 text-right font-medium">Voice</th>
                          <th className="py-2 px-3 text-right font-medium">Chat</th>
                          <th className="py-2 pl-3 text-right font-medium">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {grouped.map((row) => (
                          <tr key={row.key} className="border-b last:border-0">
                            <td className="py-2 pr-3 font-medium">{row.label}</td>
                            <td className="py-2 px-3 text-right tabular-nums">
                              {fmtNum(row.sessions)}
                            </td>
                            <td className="py-2 px-3 text-right tabular-nums text-muted-foreground">
                              {fmtUsd(row.voice)}
                            </td>
                            <td className="py-2 px-3 text-right tabular-nums text-muted-foreground">
                              {fmtUsd(row.chat)}
                            </td>
                            <td className="py-2 pl-3 text-right font-medium tabular-nums">
                              {fmtUsd(row.total)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-3 text-[11px] text-muted-foreground">
                    Each session's cost is attributed to its last-activity{" "}
                    {view === "month" ? "month" : "day"}. Widen the window or use a
                    custom range to span more {view === "month" ? "months" : "days"}.
                  </p>
                </>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default UserCostDrawer;
